import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Container } from '@mui/material';
import './App.css';
import { auth, googleAuthProvider, signUpWithEmail, signInWithEmail } from './firebase.ts';
import { signInWithPopup, signOut, setPersistence, browserLocalPersistence, getRedirectResult } from "firebase/auth";
import { Routes, Route, useNavigate } from 'react-router-dom';
import AuthPage from './AuthPage';
import Home from './Home';
import Reports from './Reports';
import Settings from './Settings';
import ChecklistsPage from './ChecklistsPage'; // Import the new ChecklistsPage
import TaskDetailView from './TaskDetailView'; // Import TaskDetailView
import ScheduledTasksPage from './ScheduledTasksPage'; // Import ScheduledTasksPage
import Header from './components/Header'; // Import the new Header component
import BottomNavigationBar from './components/BottomNavigationBar'; // Import the new BottomNavigationBar component
import { User } from 'firebase/auth'; // Move to top
import { ThemeProvider } from './ThemeContext'; // Import ThemeProvider
import useMediaQuery from '@mui/material/useMediaQuery'; // Import useMediaQuery
import { useTheme as useMuiTheme } from '@mui/material/styles'; // Import useMuiTheme

const NotificationsPage = lazy(() => import('./NotificationsPage')); // Lazy load NotificationsPage

function App() {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      if (user) {
        setUser(user);
        console.log("User signed in:", user);
      } else {
        setUser(null);
        console.log("User signed out");
      }
    });

    return () => unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithPopup(auth, googleAuthProvider);
    } catch (error) {
      console.error("Error signing in with Google", error);
    }
  };

  const handleEmailSignIn = async (email: string, password: string) => {
    try {
      await signInWithEmail(email, password);
    } catch (error: any) {
      console.error("Error signing in with email", error);
      throw error;
    }
  };

  const handleEmailSignUp = async (email: string, password: string, profileName: string) => {
    try {
      await signUpWithEmail(email, password, profileName);
    } catch (error: any) {
      console.error("Error signing up with email", error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      if (user) {
        setUser(user);
        console.log("User signed in:", user);
      } else {
        setUser(null);
        console.log("User signed out");
      }
    });

    // Handle redirect result if any
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          // This is a redirect back from a sign-in operation
          console.log("Redirect sign-in successful:", result.user);
          setUser(result.user);
        }
      })
      .catch((error) => {
        console.error("Error during redirect sign-in:", error);
      });

    return () => unsubscribe();
  }, []);

  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  return (
    <ThemeProvider>
      {!isMobile && <Header user={user} />} {/* Render Header only on non-mobile */}
      <Container className={`main-content-container ${isMobile ? 'mobile-padding-bottom' : ''}`} sx={{ boxShadow: 'none' }}> {/* Add a class for styling and remove shadow */}
        {user ? (
          <>
            <Suspense fallback={<div>Loading...</div>}> {/* Add Suspense for lazy loading */}
              <Routes>
                <Route path="/" element={<Home user={user} />} /> {/* Home Dashboard */}
                <Route path="/checklists" element={<ChecklistsPage user={user} />} /> {/* New Checklists Page */}
                <Route path="/checklists/:checklistId/tasks/:taskId" element={<TaskDetailView user={user} />} /> {/* Task Detail View */}
                <Route path="/reports" element={<Reports user={user} />} /> {/* Reports Page */}
                <Route path="/settings" element={<Settings user={user} handleSignOut={handleSignOut} />} /> {/* Settings Page */}
                <Route path="/notifications" element={<NotificationsPage />} /> {/* Notifications Page */}
                <Route path="/scheduled-tasks" element={<ScheduledTasksPage user={user} />} /> {/* New Scheduled Tasks Page */}
              </Routes>
            </Suspense>
            {isMobile && <BottomNavigationBar />} {/* Render BottomNavigationBar only on mobile */}
          </>
        ) : (
          <AuthPage
            onSignIn={handleEmailSignIn}
            onSignUp={handleEmailSignUp}
            onGoogleSignIn={handleGoogleSignIn}
          />
        )}
      </Container>
    </ThemeProvider>
  );
}

export default App;
